import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import {
  FileText, Heart, UserPlus, Users, BarChart3, MessageCircle,
  Image as ImageIcon, Clock, ArrowRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

const EMOJI_MAP = {
  heart: '\u2764\uFE0F', fire: '\uD83D\uDD25', clap: '\uD83D\uDC4F',
  laugh: '\uD83D\uDE02', wow: '\uD83D\uDE2E', idea: '\uD83D\uDCA1'
};

const ACTIVITY_CONFIG = {
  post: { icon: FileText, color: 'text-purple-500', bg: 'bg-purple-100', label: 'Publication' },
  reaction: { icon: Heart, color: 'text-pink-500', bg: 'bg-pink-100', label: 'Reaction' },
  follow: { icon: UserPlus, color: 'text-blue-500', bg: 'bg-blue-100', label: 'Abonnement' },
  community_join: { icon: Users, color: 'text-cyan-500', bg: 'bg-cyan-100', label: 'Communaute' },
};

const formatTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return "A l'instant";
  if (diffMins < 60) return `Il y a ${diffMins}m`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays < 7) return `Il y a ${diffDays}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const ActivityItem = ({ activity, navigate }) => {
  const config = ACTIVITY_CONFIG[activity.type] || ACTIVITY_CONFIG.post;
  const Icon = config.icon;
  const data = activity.data || {};

  const handleClick = () => {
    if (activity.type === 'post' && data.post_id) navigate('/app/feed');
    if (activity.type === 'reaction' && data.post_id) navigate('/app/feed');
    if (activity.type === 'follow' && data.user_id) navigate(`/app/profile/${data.user_id}`);
    if (activity.type === 'community_join' && data.community_id) navigate(`/app/community/${data.community_id}`);
  };

  return (
    <div
      onClick={handleClick}
      className="flex items-start gap-3 p-4 rounded-xl hover:bg-gray-50 cursor-pointer transition-colors group"
      data-testid={`activity-${activity.type}`}
    >
      <div className="relative flex-shrink-0">
        <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center`}>
          {activity.type === 'reaction' && data.emoji ? (
            <span className="text-lg">{EMOJI_MAP[data.emoji] || '\u2764\uFE0F'}</span>
          ) : (
            <Icon className={`h-5 w-5 ${config.color}`} />
          )}
        </div>
        {/* Timeline connector */}
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-0.5 h-full bg-gray-100 group-last:hidden" />
      </div>

      <div className="flex-1 min-w-0 pt-0.5">
        <p className="text-sm text-gray-800 leading-relaxed">
          {activity.description || config.label}
        </p>

        {/* Extra context */}
        {activity.type === 'follow' && data.display_name && (
          <div className="flex items-center gap-2 mt-2">
            <Avatar className="h-6 w-6">
              <AvatarImage src={resolveUrl(data.avatar)} />
              <AvatarFallback className="text-xs">{data.display_name?.[0]}</AvatarFallback>
            </Avatar>
            <span className="text-xs text-purple-600 font-medium">{data.display_name}</span>
          </div>
        )}
        {activity.type === 'community_join' && data.name && (
          <div className="flex items-center gap-2 mt-2">
            <div className="w-6 h-6 rounded bg-cyan-100 flex items-center justify-center">
              <Users className="h-3 w-3 text-cyan-600" />
            </div>
            <span className="text-xs text-cyan-600 font-medium">{data.name}</span>
          </div>
        )}
        {activity.type === 'post' && data.has_media && (
          <div className="flex items-center gap-1 mt-1">
            <ImageIcon className="h-3 w-3 text-gray-400" />
            <span className="text-xs text-gray-400">Avec media</span>
          </div>
        )}

        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
          <Clock className="h-3 w-3" />
          {formatTime(activity.created_at)}
        </p>
      </div>
    </div>
  );
};

const ActivityTab = ({ userId }) => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/users/activity-feed/${userId}`, { withCredentials: true });
        setActivities(data.activities || []);
      } catch (err) {
        console.error('Error loading activity:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId]);

  // Sprint 1.0c Bug J — filter test-data placeholders (content < 3 chars,
  // empty, whitespace-only) so they don't clutter the public timeline.
  const cleanedActivities = useMemo(() => {
    return (activities || []).filter((a) => {
      const desc = (a?.description || a?.data?.content || a?.data?.text || '').toString().trim();
      // Posts MUST have meaningful text. Other types (follow / community_join /
      // reaction) carry their own metadata so we keep them as long as the
      // activity description is present (≥ 3 chars).
      if (a.type === 'post') {
        return desc.length >= 3;
      }
      // Non-post activities use the standard description line ("S'est abonné…",
      // "A rejoint…") which is always ≥ 3 chars.
      return desc.length >= 3 || a.type !== 'post';
    });
  }, [activities]);

  // Sprint 1.0c Bug J — collapse-by-default to 5 items, "Voir plus" toggles
  // the full timeline IN PLACE (no navigation away from the profile page).
  const MAX_COLLAPSED = 5;
  const visibleActivities = expanded ? cleanedActivities : cleanedActivities.slice(0, MAX_COLLAPSED);
  const hiddenCount = cleanedActivities.length - MAX_COLLAPSED;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  if (cleanedActivities.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400" data-testid="activity-empty">
        <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-50" />
        <p className="text-sm">Aucune activite recente</p>
      </div>
    );
  }

  return (
    <div className="space-y-0.5" data-testid="activity-feed">
      {visibleActivities.map((activity, idx) => (
        <ActivityItem key={`${activity.type}-${idx}`} activity={activity} navigate={navigate} />
      ))}
      {!expanded && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full mt-2 py-2 text-sm font-semibold text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors inline-flex items-center justify-center gap-1"
          data-testid="activity-see-more"
        >
          Voir plus <span className="text-xs text-purple-500">({hiddenCount})</span>
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
      {expanded && cleanedActivities.length > MAX_COLLAPSED && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="w-full mt-2 py-2 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/40 rounded-lg transition-colors"
          data-testid="activity-see-less"
        >
          Voir moins
        </button>
      )}
    </div>
  );
};

export default ActivityTab;
